import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import HubCmdLogo from "@/components/HubCmdLogo";
import { Mail, ShieldCheck, Building2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const [inviteError, setInviteError] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: "Production Manager", company: "Acme Manufacturing",
    invite_token: inviteToken,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!inviteToken) {
      setInviteError("Registration is by invitation only. Please contact TruPath Advisory to request access.");
      return;
    }
    setInviteLoading(true);
    api.get(`/invitations/by-token/${inviteToken}`)
      .then((r) => {
        setInvite(r.data);
        setForm((f) => ({ ...f, email: r.data.email, company: r.data.org_name }));
      })
      .catch((err) => setInviteError(err?.response?.data?.detail || "Invitation invalid"))
      .finally(() => setInviteLoading(false));
  }, [inviteToken]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success(invite ? `Joined ${invite.org_name}` : "Organization created");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <HubCmdLogo size={36} variant="shield" />
            <span className="font-display font-black text-lg tracking-tight"><span>hub</span><span className="text-[#ff4500]">CMD</span></span>
          </Link>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Onboard</div>
          {inviteLoading ? (
            <div className="text-sm text-[#1a1a1a]/70">Loading invitation…</div>
          ) : inviteError ? (
            <div className="bg-[#fff1ea] border border-[#ff4500]/30 rounded-xl p-4">
              <div className="font-display font-bold text-[#ff4500]">Invitation problem</div>
              <div className="text-sm text-[#1a1a1a]/75 mt-1">{inviteError}</div>
              <div className="text-[12px] text-[#1a1a1a]/60 mt-3">Ask your administrator to send a new invitation, or create a new organization below.</div>
            </div>
          ) : null}
          {invite ? (
            <div className="bg-[#1a1a1a] text-white rounded-2xl p-4 mt-3" data-testid="invitation-banner">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#ff5f15]" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff5f15]">// Invitation</div>
                  <div className="font-display font-bold text-base">Join {invite.org_name}</div>
                </div>
              </div>
              <div className="text-[12px] text-white/75 mt-2">You were invited as <b className="text-white uppercase" data-testid="invitation-role">{invite.role}</b>. This seat is reserved for <b className="text-white">{invite.email}</b>.</div>
              {invite.role === "controller" && (
                <div className="mt-2 bg-white/8 border border-white/15 rounded-lg px-3 py-2 text-[11px] text-white/85">
                  <b className="text-[#ff5f15]">Controller role</b> — you&apos;ll have access to billing, seat tokens, and member management. The first Controller is typically the person who signed the company contract. We recommend a second backup Controller per organization.
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2">
              <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">Create your command center.</h1>
              <p className="mt-3 text-sm text-[#1a1a1a]/70">
                You&apos;ll become the <b>Controller</b> of a new organization — full access to billing, seat tokens, member management, and admin tools. Each user needs their own login.
              </p>
            </div>
          )}
          <form onSubmit={submit} className="mt-7 space-y-4">
            <Field label="Full name" tid="register-name-input" type="text" value={form.name} onChange={set("name")} placeholder="Carlos Rivera" />
            <Field label="Email" tid="register-email-input" type="email" value={form.email} onChange={set("email")}
              placeholder="you@plant.com" disabled={!!invite} />
            <Field label="Password" tid="register-password-input" type="password" value={form.password} onChange={set("password")} placeholder="min 6 chars" />
            <Field label="Your role / title" tid="register-role-input" type="text" value={form.role} onChange={set("role")} placeholder="Production Manager" />
            {invite ? (
              <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
                Organization · <span className="text-[#1a1a1a] font-bold">{invite.org_name}</span>
              </div>
            ) : (
              <Field label="Company / Organization name" tid="register-company-input" type="text" value={form.company} onChange={set("company")} placeholder="Acme Manufacturing" />
            )}
            <button
              data-testid="register-submit-button"
              disabled={busy}
              className="w-full bg-[#ff4500] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#cc3500] disabled:opacity-60 transition-colors duration-150">
              {busy ? "Creating…" : invite ? `Join ${invite.org_name}` : "Create organization"}
            </button>
          </form>
          <div className="mt-6 text-sm text-[#1a1a1a]/70">
            Have an account?{" "}
            <Link to="/login" data-testid="register-to-login" className="font-bold text-[#1a1a1a] underline underline-offset-4 decoration-[#ff4500]">Sign in</Link>
          </div>
        </div>
      </div>
      <div
        className="hidden lg:block relative bg-[#1a1a1a]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,139,0.5), rgba(0,0,139,0.35)), url('https://images.unsplash.com/photo-1716643863806-989dd76ae093?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxtYW51ZmFjdHVyaW5nJTIwZmFjdG9yeSUyMGZsb29yfGVufDB8fHx8MTc4MDA0MTk5OXww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
        }}>
        <div className="absolute inset-0 industrial-grid opacity-25" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#ff5f15] mb-2">// Enterprise-ready</div>
          <div className="font-display font-black text-2xl leading-tight max-w-md">One paid seat = one login. Admins invite people. No password-sharing, no off-the-books access.</div>
          <div className="mt-4 flex items-center gap-2 text-[12px] uppercase tracking-wider text-white/75">
            <Building2 className="w-3.5 h-3.5" /> Org-level RBAC · <Mail className="w-3.5 h-3.5" /> Invite-only seats
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, tid, type, value, onChange, placeholder, disabled }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/70 mb-1.5 font-mono">{label}</label>
      <input
        data-testid={tid} required type={type} value={value} onChange={onChange}
        placeholder={placeholder} disabled={disabled}
        className={`w-full bg-white border border-[#1a1a1a]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#1a1a1a] focus:ring-1 focus:ring-[#1a1a1a] ${disabled ? "opacity-60 cursor-not-allowed" : ""}`} />
    </div>
  );
}
